import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IHotelAutocompleteItem, IHotelAutocompleteResponse } from 'src/common-types/index';
import {
  SESSION_EXPIRED_MESSAGE,
  SESSION_EXPIRED_STATUS,
  SortTypes,
  Urls,
  SESSION_SEARCH_EXPIRED_MESSAGE,
} from 'src/constants/index';
import { AppThunk } from 'src/store';
import { getHeaders, getFilters, axiosInstance } from 'src/utils/index';

export interface IFiltersState {
  propertyName: string;
  distance: number;
  starRatings: number[];
  amenities: string[];
  accommodationType: string[];
  budget: string[];
  neighbourhoods: string[];
  places: string[];
  sortBy: SortTypes;
  autocompleteItems: IHotelAutocompleteItem[];
  autocompleteLoading: boolean;
  selectedAutocompleteItem: IHotelAutocompleteItem;
  selectedAutocompleteLabel: string;
}

export const filtersInitialState: IFiltersState = {
  sortBy: SortTypes.BiggestSavingsPercentage,
  propertyName: '',
  distance: 10,
  budget: [],
  neighbourhoods: [],
  places: [],
  starRatings: [],
  amenities: [],
  accommodationType: [],
  autocompleteItems: [],
  autocompleteLoading: false,
  selectedAutocompleteItem: null,
  selectedAutocompleteLabel: '',
};

const AUTO_COMPLETE_MAX_ITEMS = 5;
const zeroItem = 0;

const filtersSlice = createSlice({
  name: 'filters',
  initialState: filtersInitialState,
  reducers: {
    setSortBy: (state: IFiltersState, { payload }: PayloadAction<SortTypes>) => {
      state.sortBy = payload;
    },
    setPropertyName: (state: IFiltersState, { payload }: PayloadAction<string>) => {
      state.propertyName = payload;
    },
    setBudget: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.budget = payload;
    },
    setNeighbourhoods: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.neighbourhoods = payload;
    },
    setPlaces: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.places = payload;
    },
    setStarRating: (state: IFiltersState, { payload }: PayloadAction<number[]>) => {
      state.starRatings = payload;
    },
    setAmenities: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.amenities = payload;
    },
    setAccommodationType: (state: IFiltersState, { payload }: PayloadAction<string[]>) => {
      state.accommodationType = payload;
    },
    setDistance: (state: IFiltersState, { payload }: PayloadAction<number>) => {
      state.distance = payload;
    },
    resetFilters: () => {
      return filtersInitialState;
    },
    setFilters: (state: IFiltersState, { payload }: PayloadAction<IFiltersState>) => {
      return payload;
    },
    setAutocompleteItems: (
      state: IFiltersState,
      { payload }: PayloadAction<IHotelAutocompleteItem[]>,
    ) => {
      state.autocompleteItems = payload;
    },
    setAutocompleteLoading: (state: IFiltersState, { payload }: PayloadAction<boolean>) => {
      state.autocompleteLoading = payload;
    },
    selectAutocompleteItem: (
      state: IFiltersState,
      { payload }: PayloadAction<IHotelAutocompleteItem>,
    ) => {
      state.selectedAutocompleteItem = payload;
    },
    selectAutocompleteLabel: (state: IFiltersState, { payload }: PayloadAction<string>) => {
      state.selectedAutocompleteLabel = payload;
    },
  },
});

export const filtersActions = filtersSlice.actions;

export const filtersReducer = filtersSlice.reducer;

export const getHotelsAutocomplete = (search: string): AppThunk => {
  return async (dispatch, getState) => {
    try {
      const { filtersStore, hotelsStore, locationsStore } = getState();
      dispatch(filtersActions.setAutocompleteLoading(true));

      const res: { data: IHotelAutocompleteResponse } = await axiosInstance.post(
        Urls.HotelAutocomplete,
        {
          count: AUTO_COMPLETE_MAX_ITEMS,
          sessionKey: hotelsStore.sessionKey,
          location: hotelsStore.lastSearchedLocation
            ? hotelsStore.lastSearchedLocation.geoLocation
            : locationsStore.selectedLocation.geoLocation,
          filter: {
            ...getFilters(filtersStore),
            propertyName: search,
          },
        },
        {
          ...getHeaders(),
        },
      );

      dispatch(filtersActions.setAutocompleteItems(res.data.hotelDetails));
      dispatch(filtersActions.setAutocompleteLoading(false));
    } catch (error) {
      dispatch(filtersActions.setAutocompleteLoading(false));
      console.error(error);

      // error when user session ended
      if (
        error?.response?.status === SESSION_EXPIRED_STATUS &&
        error?.response?.data &&
        (error?.response?.data[zeroItem] === SESSION_EXPIRED_MESSAGE ||
          error?.response?.data[zeroItem]?.errorMessage === SESSION_SEARCH_EXPIRED_MESSAGE)
      ) {
        window.location.reload();
      }
    }
  };
};
